import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';

const ChequeDDApproval = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchData, setSearchData] = useState({
    bookingId: '',
    plotNo: '',
    site: '',
    sector: '',
    block: ''
  });

  // Sample data for Plot Booking Payment Status
  const [paymentData] = useState([
    {
      id: 1,
      name: 'Rajesh Kumar',
      bookingId: 'BKG001',
      plotAmount: 500000,
      bookingAmount: 50000,
      paidAmount: 50000,
      paymentMode: 'Cheque',
      paymentDetails: 'CHQ123456',
      branchName: 'HDFC Bank - Main Branch',
      plotBooking: 'A-101, Sector 5',
      paymentDate: '2024-10-01',
      paymentStatus: 'Pending',
      isFormUploaded: true,
    },
    {
      id: 2,
      name: 'Priya Sharma',
      bookingId: 'BKG002',
      plotAmount: 750000,
      bookingAmount: 75000,
      paidAmount: 75000,
      paymentMode: 'DD',
      paymentDetails: 'DD789012',
      branchName: 'SBI - Central Branch',
      plotBooking: 'B-205, Sector 3',
      paymentDate: '2024-10-03',
      paymentStatus: 'Approved',
      isFormUploaded: true,
    },
    {
      id: 3,
      name: 'Amit Patel',
      bookingId: 'BKG003',
      plotAmount: 600000,
      bookingAmount: 60000,
      paidAmount: 60000,
      paymentMode: 'Cheque',
      paymentDetails: 'CHQ345678',
      branchName: 'ICICI Bank - City Branch',
      plotBooking: 'C-150, Sector 7',
      paymentDate: '2024-10-05',
      paymentStatus: 'Rejected',
      isFormUploaded: false,
    },
    {
      id: 4,
      name: 'Sunita Verma',
      bookingId: 'BKG004',
      plotAmount: 800000,
      bookingAmount: 80000,
      paidAmount: 80000,
      paymentMode: 'DD',
      paymentDetails: 'DD456789',
      branchName: 'Axis Bank - Metro Branch',
      plotBooking: 'D-301, Sector 2',
      paymentDate: '2024-10-07',
      paymentStatus: 'Pending',
      isFormUploaded: true,
    },
    {
      id: 5,
      name: 'Vikash Singh',
      bookingId: 'BKG005',
      plotAmount: 450000,
      bookingAmount: 45000,
      paidAmount: 45000,
      paymentMode: 'Cheque',
      paymentDetails: 'CHQ567890',
      branchName: 'PNB - Regional Branch',
      plotBooking: 'E-125, Sector 4',
      paymentDate: '2024-10-08',
      paymentStatus: 'Pending',
      isFormUploaded: false,
    },
  ]);

  const siteOptions = ['Site A', 'Site B', 'Site C', 'Site D'];
  const sectorOptions = ['Sector 1', 'Sector 2', 'Sector 3', 'Sector 4', 'Sector 5', 'Sector 6', 'Sector 7'];
  const blockOptions = ['Block A', 'Block B', 'Block C', 'Block D', 'Block E'];

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (field, value) => {
    setSearchData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    console.log('Searching with data:', searchData);
    // Implement search functionality
  };

  const handleApprove = (id) => {
    console.log('Approving payment with id:', id);
    // Implement approve functionality
  };

  const handleReject = (id) => {
    console.log('Rejecting payment with id:', id);
    // Implement reject functionality
  };

  const handleViewForm = (id) => {
    console.log('Viewing form for id:', id);
    // Implement view form functionality
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Plot Booking Payment Status
      </Typography>

      {/* Search Form */}
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Search Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              label="Booking ID"
              value={searchData.bookingId}
              onChange={(e) => handleInputChange('bookingId', e.target.value)}
              size="small"
              placeholder="Enter Booking ID"
            />
          </Grid>
          
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              label="Plot No"
              value={searchData.plotNo}
              onChange={(e) => handleInputChange('plotNo', e.target.value)}
              size="small"
              placeholder="Enter Plot Number"
            />
          </Grid>
          
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Site</InputLabel>
              <Select
                value={searchData.site}
                label="Site"
                onChange={(e) => handleInputChange('site', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select Site</em>
                </MenuItem>
                {siteOptions.map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sector</InputLabel>
              <Select
                value={searchData.sector}
                label="Sector"
                onChange={(e) => handleInputChange('sector', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select Sector</em>
                </MenuItem>
                {sectorOptions.map((sector) => (
                  <MenuItem key={sector} value={sector}>
                    {sector}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Block</InputLabel>
              <Select
                value={searchData.block}
                label="Block"
                onChange={(e) => handleInputChange('block', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select Block</em>
                </MenuItem>
                {blockOptions.map((block) => (
                  <MenuItem key={block} value={block}>
                    {block}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={12} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              sx={{ py: 0.5, fontSize: '0.8rem', width: 'auto', minWidth: '100px' }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Status Table */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Payment Approval Data
        </Typography>
        
        <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
          Total Records: {paymentData.length}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Plot Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Booking Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Paid Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Mode</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Branch Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Plot Booking</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Form Uploaded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow 
                    key={row.id}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {row.bookingId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(row.plotAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(row.bookingAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                        {formatCurrency(row.paidAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.paymentMode} 
                        size="small" 
                        color={row.paymentMode === 'Cheque' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {row.paymentDetails}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {row.branchName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.plotBooking}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.paymentDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.paymentStatus} 
                        size="small" 
                        color={getStatusColor(row.paymentStatus)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleApprove(row.id)}
                            disabled={row.paymentStatus === 'Approved'}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleReject(row.id)}
                            disabled={row.paymentStatus === 'Rejected'}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={row.isFormUploaded ? 'Yes' : 'No'} 
                          size="small" 
                          color={row.isFormUploaded ? 'success' : 'error'}
                        />
                        {row.isFormUploaded && (
                          <Tooltip title="View Form">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewForm(row.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={paymentData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />
      </Paper>
    </Box>
  );
};

export default ChequeDDApproval;